import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useFollowStatus, useToggleFollow } from "@/hooks/use-followed-animals";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface FollowAnimalButtonProps {
  animalId: string;
  animalName?: string;
  variant?: "icon" | "full" | "menu-item";
  className?: string;
}

export function FollowAnimalButton({
  animalId,
  animalName,
  variant = "icon",
  className,
}: FollowAnimalButtonProps) {
  const isFollowing = useFollowStatus(animalId);
  const toggleFollow = useToggleFollow(animalId);
  const { toast } = useToast();

  const handleClick = () => {
    toggleFollow.mutate(undefined, {
      onSuccess: (result: { following: boolean }) => {
        const name = animalName || animalId;
        if (result?.following) {
          toast({ title: `Following ${name}`, description: "You'll receive notifications for this animal." });
        } else {
          toast({ title: `Unfollowed ${name}`, description: "You'll no longer receive notifications." });
        }
      },
      onError: () => {
        toast({ title: "Something went wrong", description: "Could not update follow status. Please try again.", variant: "destructive" });
      },
    });
  };

  if (variant === "menu-item") {
    return (
      <DropdownMenuItem
        onClick={handleClick}
        disabled={toggleFollow.isPending}
        className={className}
        data-testid={`button-follow-animal-${animalId}`}
      >
        <Heart
          className={cn(
            "h-4 w-4 mr-2",
            isFollowing && "fill-current text-red-500 dark:text-red-400"
          )}
        />
        {isFollowing ? "Unfollow" : "Follow"}
      </DropdownMenuItem>
    );
  }

  if (variant === "full") {
    return (
      <Button
        variant="outline"
        onClick={handleClick}
        disabled={toggleFollow.isPending}
        className={className}
        data-testid={`button-follow-animal-${animalId}`}
      >
        <Heart
          className={cn(
            "h-4 w-4 mr-2",
            isFollowing && "fill-current text-red-500 dark:text-red-400"
          )}
        />
        {isFollowing ? "Following" : "Follow"}
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleClick}
      disabled={toggleFollow.isPending}
      title={isFollowing ? "Unfollow" : "Follow"}
      className={className}
      data-testid={`button-follow-animal-${animalId}`}
    >
      <Heart
        className={cn(
          "h-4 w-4",
          isFollowing && "fill-current text-red-500 dark:text-red-400"
        )}
      />
    </Button>
  );
}
